import { Grid } from '../../core/Grid.js';
import {
    Position,
    positionKey,
    positionsEqual,
    parsePositionKey,
} from '../../core/GridGeometry.js';
import { AntennasGame } from './AntennasGame.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

export class AntennasVortex extends AntennasGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates);
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, paired) => {
            // No antenna on either gate cell
            if (this.getAntennaAtCell(p)) return false;
            if (this.getAntennaAtCell(paired)) return false;
            return true;
        });
    }

    toggleVortexGate(pos: Position): boolean {
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos));
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // ── Scoring ────────────────────────────────────────────────────

    override getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const antennas = this.getAntennas();
        const digitPositions = this.getDigitPositions();
        const grid = this.getGrid();

        // A = total antenna cells
        let antennaCells = 0;
        for (const [, antenna] of antennas) {
            antennaCells += antenna.path.length;
        }

        // B = empty cells visited by signals
        const signalCells = new Set<string>();
        for (const [, antenna] of antennas) {
            const from = this.getSignalEnd(antenna);
            let current = { ...from };
            const visited = new Set<string>();

            while (true) {
                current = this.wrapMove(current, antenna.signalDirection);
                const teleportExit = this.checkVortexTeleport(current);
                if (teleportExit) current = teleportExit;

                const key = positionKey(current);
                if (visited.has(key)) break;
                visited.add(key);

                // Check if we hit another antenna
                let hitAntenna = false;
                for (const [, other] of antennas) {
                    if (other.id !== antenna.id) {
                        for (const cell of other.path) {
                            if (positionsEqual(current, cell)) {
                                hitAntenna = true;
                                break;
                            }
                        }
                    }
                }
                if (hitAntenna) break;

                // Check if not a digit cell
                let isDigit = false;
                for (const [, digitPos] of digitPositions) {
                    if (positionsEqual(current, digitPos)) {
                        isDigit = true;
                        break;
                    }
                }
                if (!isDigit) {
                    signalCells.add(key);
                }

                if (visited.size > grid.size * grid.size) break;
            }
        }

        return calculateScore(antennaCells, this.gm.deduplicateCellCount(signalCells));
    }

    // ── Placement Blocking ────────────────────────────────────────

    protected override isValidAntennaCell(pos: Position): boolean {
        if (this.gm.isActiveGate(pos)) return false;
        return super.isValidAntennaCell(pos);
    }

    // ── Teleportation ─────────────────────────────────────────────

    protected override checkVortexTeleport(position: Position): Position | null {
        if (!this.gm.hasActiveGates()) return null;

        const key = positionKey(position);
        const pairedKey = this.gm.raw.get(key);
        if (pairedKey) {
            return parsePositionKey(pairedKey);
        }
        return null;
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
    }
}
